import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { resolveImageUrl } from "../lib/api";
import LazyImage from "../components/LazyImage";
import BeforeAfterSlider from "../components/BeforeAfterSlider";
import Lightbox from "../components/Lightbox";
import FloatingInquiry from "../components/FloatingInquiry";
import ProjectCard from "../components/ProjectCard";
import { Heart, MapPin, Calendar, Square, Star, ArrowLeft } from "lucide-react";
import { useSaved } from "../hooks/useSaved";

export default function ProjectDetail() {
    const { slug } = useParams();
    const [project, setProject] = useState(null);
    const [similar, setSimilar] = useState([]);
    const [lbIdx, setLbIdx] = useState(null);
    const { isSaved, toggle } = useSaved();

    useEffect(() => {
        let ignore = false;
        setProject(null);
        (async () => {
            try {
                const r = await api.get(`/projects/${slug}`);
                if (!ignore) setProject(r.data);
                const s = await api.get(`/projects/${slug}/similar`);
                if (!ignore) setSimilar(s.data || []);
            } catch {}
        })();
        return () => { ignore = true; };
    }, [slug]);

    useEffect(() => {
        if (project?.title) document.title = `${project.title} · TileSphere`;
    }, [project]);

    if (!project) {
        return (
            <main className="min-h-screen bg-black flex items-center justify-center">
                <div className="overline">Loading…</div>
            </main>
        );
    }

    const gallery = project.gallery && project.gallery.length > 0 ? project.gallery : [{ url: project.thumbnail_url }];
    const saved = isSaved(project.id);

    return (
        <main data-testid="project-detail-page" className="bg-black text-white">
            {/* Hero image */}
            <section className="relative h-[88vh] min-h-[560px] w-full overflow-hidden">
                <motion.div
                    initial={{ scale: 1.08, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0"
                >
                    <LazyImage src={project.thumbnail_url} alt={project.title} className="w-full h-full" />
                    <div className="absolute inset-0 hero-overlay" />
                </motion.div>

                <div className="relative h-full container-luxury flex flex-col justify-end pb-20 pt-32">
                    <Link to="/explore" className="overline mb-6 hover:text-gold inline-flex items-center gap-2">
                        <ArrowLeft size={14} /> Back to explorer
                    </Link>
                    <div className="overline text-gold mb-3">{project.room_type} · {project.company_name}</div>
                    <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] max-w-4xl text-shadow-soft" data-testid="project-title">
                        {project.title}
                    </h1>
                    <div className="mt-8 flex flex-wrap gap-3">
                        {project.style_tags?.map((t) => (
                            <Link key={t} to={`/explore?style=${encodeURIComponent(t)}`} className="text-xs tracking-[0.2em] uppercase border border-white/20 px-3 py-1.5 hover:border-gold hover:text-gold transition-colors">{t}</Link>
                        ))}
                    </div>
                </div>
            </section>

            <section className="container-luxury py-20 grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-16">
                <div>
                    <div className="overline mb-4">The Project</div>
                    <p className="font-serif text-2xl md:text-3xl leading-relaxed text-white/90 max-w-3xl">{project.description}</p>

                    {/* Gallery */}
                    <div className="mt-16">
                        <div className="overline mb-6">Gallery</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4" data-testid="project-gallery">
                            {gallery.map((g, i) => (
                                <button
                                    key={i}
                                    onClick={() => setLbIdx(i)}
                                    data-testid={`gallery-item-${i}`}
                                    className="relative group overflow-hidden bg-[#0a0a0a]"
                                    style={{ aspectRatio: i % 3 === 0 ? "4/5" : "3/4" }}
                                >
                                    <div className="w-full h-full transform transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]">
                                        <LazyImage src={g.url || g} className="w-full h-full" />
                                    </div>
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-500" />
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Before/After */}
                    {project.before_url && project.after_url && (
                        <div className="mt-20">
                            <div className="overline mb-6">Before / After</div>
                            <BeforeAfterSlider beforeUrl={project.before_url} afterUrl={project.after_url} />
                        </div>
                    )}
                </div>

                {/* Sidebar meta */}
                <aside className="lg:sticky lg:top-28 self-start">
                    <div className="border border-white/10 p-8 space-y-6 bg-[#0a0a0a]">
                        <div>
                            <div className="overline mb-3">Project Details</div>
                            <ul className="space-y-3 text-sm">
                                <Meta icon={<MapPin size={14} />} label="Location" value={project.location || "—"} />
                                <Meta icon={<Calendar size={14} />} label="Completed" value={project.completion_date || "—"} />
                                <Meta icon={<Square size={14} />} label="Area" value={project.area_sqm ? `${project.area_sqm} sqm` : "—"} />
                                <Meta icon={<Star size={14} />} label="Luxury" value={"★".repeat(project.luxury_level || 0)} />
                            </ul>
                        </div>

                        <div className="border-t border-white/10 pt-6">
                            <div className="overline mb-3">Surface</div>
                            <div className="text-white text-base">{project.tile_brand || "—"}</div>
                            {project.tile_collection && <div className="text-white/60 text-sm">{project.tile_collection}</div>}
                        </div>

                        <div className="border-t border-white/10 pt-6">
                            <div className="overline mb-3">Provided by</div>
                            <Link to={`/explore?company=${project.company_id}`} className="text-gold tracking-[0.15em] uppercase text-sm hover:text-gold-soft">{project.company_name}</Link>
                            {project.agent_name && <div className="text-white/60 text-sm mt-1">Agent: {project.agent_name}</div>}
                        </div>

                        <button
                            onClick={() => toggle(project)}
                            data-testid="project-save-toggle"
                            className={`w-full inline-flex items-center justify-center gap-2 py-3 border transition-colors text-sm tracking-[0.2em] uppercase ${
                                saved ? "border-gold text-gold" : "border-white/20 text-white hover:border-gold hover:text-gold"
                            }`}
                        >
                            <Heart size={14} fill={saved ? "currentColor" : "transparent"} /> {saved ? "Saved" : "Save Inspiration"}
                        </button>
                    </div>
                </aside>
            </section>

            {/* Similar */}
            {similar.length > 0 && (
                <section className="container-luxury pb-32" data-testid="similar-section">
                    <div className="flex items-end justify-between mb-10">
                        <div>
                            <div className="overline mb-3">Find Similar Style</div>
                            <h2 className="font-serif text-4xl tracking-tight">More from this aesthetic.</h2>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {similar.slice(0, 4).map((p, i) => <ProjectCard key={p.id} project={p} index={i} />)}
                    </div>
                </section>
            )}

            {lbIdx != null && (
                <Lightbox
                    images={gallery}
                    index={lbIdx}
                    onClose={() => setLbIdx(null)}
                    onPrev={() => setLbIdx((i) => (i - 1 + gallery.length) % gallery.length)}
                    onNext={() => setLbIdx((i) => (i + 1) % gallery.length)}
                />
            )}

            <FloatingInquiry projectId={project.id} projectTitle={project.title} />
        </main>
    );
}

function Meta({ icon, label, value }) {
    return (
        <li className="flex items-start gap-3">
            <span className="text-gold mt-0.5">{icon}</span>
            <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-white/45">{label}</div>
                <div className="text-white">{value}</div>
            </div>
        </li>
    );
}
